import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './styles/theme.css';

// ── Stores ──
import useAppStore from './stores/useAppStore';
import useAuthStore from './stores/useAuthStore';

// ── Layout (eager — always needed) ──
import Nav from './components/layout/Nav';
import Footer from './components/layout/Footer';
import OtpVerification from './components/common/OtpVerification';
import { PageLoader } from './components/common/Skeleton';

// ── Pages (lazy — loaded on demand) ──
const HomePage               = lazy(() => import('./pages/HomePage'));
const ProgramsPage           = lazy(() => import('./pages/ProgramsPage'));
const ProgramDetailPage      = lazy(() => import('./pages/ProgramDetailPage'));
const AboutPage              = lazy(() => import('./pages/AboutPage'));
const EventDetailPageWrapper = lazy(() => import('./pages/EventDetailPage'));
const LoginPage              = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage           = lazy(() => import('./pages/auth/RegisterPage'));
const SpeakerRegisterPage    = lazy(() => import('./pages/auth/SpeakerRegisterPage'));
const AttendeeRegisterPage   = lazy(() => import('./pages/auth/AttendeeRegisterPage'));
const SpeakerDashboard       = lazy(() => import('./pages/dashboard/SpeakerDashboard'));
const AttendeeDashboard      = lazy(() => import('./pages/dashboard/AttendeeDashboard'));
const ApplySpeakerPage       = lazy(() => import('./pages/ApplySpeakerPage'));
const AdminDashboard         = lazy(() => import('./pages/admin/AdminDashboard'));

const AUTH_PATHS = ['/login', '/register', '/register/speaker', '/register/attendee',
  '/dashboard/speaker', '/dashboard/attendee', '/admin'];

function S({ children }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}


function RequireRole({ role, children }) {
  const { user, initializing } = useAuthStore();
  if (initializing) return null;
  if (!user) return <Navigate to="/login" replace />;
  
  // Speakers are allowed to access Attendee-specific routes
  if (role === 'ATTENDEE' && user.role === 'SPEAKER') return children;
  
  if (user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function RedirectIfAuth({ children }) {
  const { user } = useAuthStore();
  if (user) {
    const target = user.role === 'SPEAKER' ? '/dashboard/speaker'
      : user.role === 'ATTENDEE' ? '/dashboard/attendee'
      : user.role === 'ADMIN' ? '/admin' : '/';
    return <Navigate to={target} replace />;
  }
  return children;
}

// ── Scroll restoration ──
function ScrollToTop() {
  const { pathname } = useLocation();
  const { scrollToBoard, setScrollToBoard } = useAppStore();

  useEffect(() => {
    if (scrollToBoard && pathname === '/about') {
      const t = setTimeout(() => {
        const el = document.getElementById('board-ambassadors-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setScrollToBoard(false);
      }, 120);
      return () => clearTimeout(t);
    } else if (!scrollToBoard) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pathname, scrollToBoard, setScrollToBoard]);

  return null;
}

export default function App() {
  const { initializing, initialize, otpSent } = useAuthStore();
  const location = useLocation();

  // Initialize auth from cookie on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Listen for forced logout (401 interceptor)
  useEffect(() => {
    const handler = () => {
      useAuthStore.getState().logout();
    };
    window.addEventListener('cxo_logout', handler);
    return () => window.removeEventListener('cxo_logout', handler);
  }, []);

  const isAuthPage = AUTH_PATHS.some(p => location.pathname.startsWith(p));

  // Show loading screen while checking auth
  if (initializing) {
    return (
      <div style={{
        minHeight: '100vh', background: '#050d1a', display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.5 }}>
          <span style={{ color: '#C9A96E' }}>CXO</span>
          <span style={{ color: '#fff', fontWeight: 300, fontStyle: 'italic' }}>Era</span>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, letterSpacing: '0.2em' }}>Loading...</div>
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Nav />
      <main>
        <Routes>
          <Route path="/" element={<S><HomePage /></S>} />
          <Route path="/programs" element={<S><ProgramsPage /></S>} />
          <Route path="/programs/:role" element={<S><ProgramDetailPage /></S>} />
          <Route path="/events/:id" element={<S><EventDetailPageWrapper /></S>} />
          <Route path="/about" element={<S><AboutPage /></S>} />

          {/* Auth pages — redirect away if already logged in */}
          <Route path="/login" element={<RedirectIfAuth><S><LoginPage /></S></RedirectIfAuth>} />
          <Route path="/register" element={<RedirectIfAuth><S><RegisterPage /></S></RedirectIfAuth>} />
          <Route path="/register/speaker" element={
            <RedirectIfAuth><S>{otpSent ? <OtpVerification /> : <SpeakerRegisterPage />}</S></RedirectIfAuth>
          } />
          <Route path="/register/attendee" element={
            <RedirectIfAuth><S>{otpSent ? <OtpVerification /> : <AttendeeRegisterPage />}</S></RedirectIfAuth>
          } />

          {/* Protected dashboards */}
          <Route path="/dashboard/speaker" element={
            <RequireRole role="SPEAKER"><S><SpeakerDashboard /></S></RequireRole>
          } />
          <Route path="/dashboard/attendee" element={
            <RequireRole role="ATTENDEE"><S><AttendeeDashboard /></S></RequireRole>
          } />
          <Route path="/apply-speaker" element={
            <RequireRole role="ATTENDEE"><S><ApplySpeakerPage /></S></RequireRole>
          } />
          <Route path="/admin" element={
            <RequireRole role="ADMIN">
              <S><AdminDashboard /></S>
            </RequireRole>
          } />

          {/* Catch-all → home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isAuthPage && !location.pathname.startsWith('/admin') && <Footer />}
    </>
  );
}
